import React, { Component, PropTypes } from 'react';
import { Paper, IconButton } from 'material-ui';
import * as SvgIcons from 'components/Material/SvgIcons';
import classNames from 'classnames';
import Css from 'Css';
import Portal from 'components/Portal';
import CSSPropertyOperations from 'react/lib/CSSPropertyOperations';
const { Units } = Css;

const css = oxygenCss({
  root: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    overflow: 'auto',
    '&overlay': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)'
    },
    textAlign: 'center',
    '&hasToolbar': {
      ' container': {
        top: 48,
        paddingTop: 48
      }
    }
  },
  container: {
    // paddingTop: 48,
  },
  padded: {
    padding: Units.base
  },
  overlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  },
  dialogWrapper: {
    position: 'relative',
    boxShadow:
      '0 3px 14px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.11)',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    overflow: 'hidden'
  },
  toolbar: {
    // height: 48,
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    backgroundColor: '#00c9cc',
    color: '#FFFFFF'
  },
  title: {
    position: 'absolute',
    lineHeight: '48px',
    padding: `0 ${Units.base * 2}px`,
    fontSize: '20px',
    right: 48,
    left: 0,
    bottom: 0,
    top: 0
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 48
  },
  dialog: {
    display: 'inline-block',
    textAlign: 'left',
    maxWidth: 640,
    margin: `${Units.keyline}px auto`,
    '&maxi': {
      width: 'auto',
      minWidth: Units.keyline * 10,
      margin: Units.keyline,
      '@phone': {
        maxWidth: 'auto',
        minWidth: Units.keyline * 2,
        margin: Units.base
      }
    },
    '&mini': {
      width: Units.keyline * 5,
      bottom: 'auto'
    },
    '@phone': {
      // position: 'absolute',
      margin: Units.base * 2
      // width: 'auto',
      // left: Units.base * 2,
      // right: Units.base * 2,
      // top: Units.base * 2,
      // bottom: Units.base * 2,
    }
  }
});

const ESC = 27;

const isDescendant = (parent, child) => {
  let node = child.parentNode;
  while (node !== null) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
};

class FullScreenDialog extends Component {
  static propTypes = {
    onRequestClose: PropTypes.func,
    children: PropTypes.node,
    title: PropTypes.node,
    open: PropTypes.bool,
    overlay: PropTypes.bool,
    onDismiss: PropTypes.func,
    toolbar: PropTypes.bool,
    mini: PropTypes.bool,
    maxi: PropTypes.bool,
    padded: PropTypes.bool
  };

  static contextTypes = {
    media: PropTypes.string,
    muiTheme: PropTypes.object
  };

  static defaultProps = {
    toolbar: true,
    closeButton: true,
    overlay: true
  };

  componentDidMount() {
    const { overlay, open } = this.props;
    overlay && document.addEventListener('keyup', this.handleKey);
    document.addEventListener('mousedown', this.handleClick);
    document.addEventListener('touchstart', this.handleTouchStart);
    // Needs to defer touchend event listener, so we do not catch the end of the event that triggered the dialog appearance
    // Otherwise, it would close the dialog as soon as it opens
    setTimeout(function() {
      document.addEventListener('touchend', this.handleTouchEnd);
    }, 0);
    if (open) {
      this.open();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.open && !nextProps.open) {
      this.close();
    } else if (!this.props.open && nextProps.open) {
      this.open();
    }
  }

  componentWillUnmount() {
    const { overlay } = this.props;
    overlay && document.removeEventListener('keyup', this.handleKey);

    document.removeEventListener('mousedown', this.handleClick);
    document.removeEventListener('touchstart', this.handleTouchStart);
    document.removeEventListener('touchend', this.handleTouchEnd);

    this.close(); // Solves #1302 (removes 'overflow: hidden' properly)
  }

  close = () => {
    const { onDismiss } = this.props;
    CSSPropertyOperations.setValueForStyles(document.body, {
      overflow: this._overflow
    });
    onDismiss && onDismiss();
  };

  open = () => {
    this._overflow = document.body.style.overflow;
    CSSPropertyOperations.setValueForStyles(document.body, {
      overflow: 'hidden'
    });
  };

  handleKey = event => {
    const { keyCode } = event;
    if (keyCode === ESC) {
      this.requestClose();
    }
  };

  handleTouchStart = event => {
    const touchObj = event.changedTouches[0];
    this._pageX = touchObj.pageX;
  };

  handleTouchEnd = event => {
    const touchObj = event.changedTouches[0];
    if (this._pageX === touchObj.pageX) {
      this.handleClick(event);
    }
  };

  handleClick = event => {
    if (!isDescendant(this.dialog, event.target)) {
      this.requestClose();
    }
  };

  requestClose = event => {
    const { onRequestClose } = this.props;
    event && event.preventDefault();
    event && event.stopPropagation();
    onRequestClose && onRequestClose();
  };

  render() {
    const {
      mini,
      maxi,
      onRequestClose,
      padded,
      overlay,
      children,
      title,
      open,
      toolbar
    } = this.props;
    if (!open) {
      return null;
    }
    const classes = classNames(css.root, {
      [css.overlay]: overlay
    });
    return (
      <Portal dialog className={classes}>
        <div
          className={classNames(css.dialog, {
            [css.mini]: mini,
            [css.maxi]: maxi
          })}
        >
          <div
            className={css.dialogWrapper}
            ref={dialog => (this.dialog = dialog)}
          >
            {toolbar && (
              <div className={css.toolbar}>
                <div className={css.title}>{title}</div>
                {onRequestClose && (
                  <div className={css.closeButton}>
                    <IconButton onTouchTap={this.requestClose}>
                      <SvgIcons.NavigationClose />
                    </IconButton>
                  </div>
                )}
              </div>
            )}
            <div className={classNames(css.container, padded && css.padded)}>
              {children}
            </div>
          </div>
        </div>
      </Portal>
    );
  }
}

export default FullScreenDialog;



// WEBPACK FOOTER //
// ./src/js/components/FullScreenDialog.js